/**
 * The calendar view's arithmetic (ACL-292): which days a Day, Week or Month view covers, how it
 * steps, and where each appointment sits in a day's hour grid. On the viewer's own clock, as
 * GoHighLevel's calendar view is (it labels the column "GMT-05:00"); the list and the details keep
 * each calendar's clock. Pure, for `test/calendar-view.test.ts`.
 */

export type ViewMode = "day" | "week" | "month";

export const VIEW_LABELS: Record<ViewMode, string> = { day: "Day view", week: "Week view", month: "Month view" };

const MINUTE = 60_000;

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n, d.getHours(), d.getMinutes());
}

/** The first day of `d`'s week, weeks starting on `weekStartsOn` (0 is Sunday, as `getDay`). */
export function startOfWeek(d: Date, weekStartsOn = 0): Date {
  const day = startOfDay(d);
  return addDays(day, -((day.getDay() - weekStartsOn + 7) % 7));
}

export function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** The days a view shows, and the instants to ask the API for: `end` is exclusive. A month view
 *  shows whole weeks, so it starts before the 1st and ends after the last. */
export function viewRange(mode: ViewMode, anchor: Date, weekStartsOn = 0): { start: Date; end: Date; days: Date[] } {
  let first: Date;
  let count: number;
  if (mode === "day") {
    first = startOfDay(anchor);
    count = 1;
  } else if (mode === "week") {
    first = startOfWeek(anchor, weekStartsOn);
    count = 7;
  } else {
    const firstOfMonth = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const lastOfMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    first = startOfWeek(firstOfMonth, weekStartsOn);
    const weeks = Math.ceil((Math.round((startOfDay(lastOfMonth).getTime() - first.getTime()) / 86_400_000) + 1) / 7);
    count = weeks * 7;
  }
  const days = Array.from({ length: count }, (_, i) => addDays(first, i));
  return { start: first, end: addDays(first, count), days };
}

/** The anchor one step back (-1) or on (+1): a day, a week or a month. */
export function step(mode: ViewMode, anchor: Date, dir: -1 | 1): Date {
  if (mode === "day") return addDays(anchor, dir);
  if (mode === "week") return addDays(anchor, 7 * dir);
  const next = new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1);
  return next;
}

/** "September 2026", "Sep 27 – Oct 3, 2026", "Tue, Sep 29, 2026". */
export function rangeLabel(mode: ViewMode, anchor: Date, weekStartsOn = 0, locale = "en-US"): string {
  if (mode === "month") return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(anchor);
  if (mode === "day") {
    return new Intl.DateTimeFormat(locale, { weekday: "short", month: "short", day: "numeric", year: "numeric" }).format(anchor);
  }
  const { days } = viewRange("week", anchor, weekStartsOn);
  const first = days[0]!;
  const last = days[6]!;
  const md = (d: Date) => new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" }).format(d);
  return `${md(first)} – ${md(last)}, ${last.getFullYear()}`;
}

/** "GMT-05:00": the viewer's offset on `d`, as the hour column is labelled. */
export function gmtLabel(d: Date): string {
  const offset = -d.getTimezoneOffset();
  const sign = offset < 0 ? "-" : "+";
  const abs = Math.abs(offset);
  return `GMT${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
}

/** Anything with a start and an end: an appointment, a block. */
export interface Timed {
  id: string;
  start: Date;
  end: Date;
}

/** The items that overlap `day`, earliest first. */
export function onDay<T extends Timed>(items: readonly T[], day: Date): T[] {
  const from = startOfDay(day).getTime();
  const to = addDays(startOfDay(day), 1).getTime();
  return items
    .filter((i) => i.start.getTime() < to && i.end.getTime() > from)
    .sort((a, b) => a.start.getTime() - b.start.getTime() || b.end.getTime() - a.end.getTime());
}

export interface Placed<T> {
  item: T;
  /** Minutes from the day's midnight, clipped to the day. */
  top: number;
  height: number;
  /** Which column of `columns` it takes: overlapping items sit side by side. */
  column: number;
  columns: number;
}

/**
 * Where each item of one day goes in the hour grid. Items that overlap share the width, each in
 * the first column free at its start; a run of items that overlap one another is one group, and
 * every item in a group is as wide as the group needs. At least 15 minutes tall, to be clickable.
 */
export function layoutDay<T extends Timed>(items: readonly T[], day: Date): Placed<T>[] {
  const midnight = startOfDay(day).getTime();
  const sorted = onDay(items, day);
  const placed: Placed<T>[] = [];
  let group: Placed<T>[] = [];
  let groupEnd = -Infinity;
  const columnEnds: number[] = [];

  const close = () => {
    const width = Math.max(1, ...group.map((p) => p.column + 1));
    for (const p of group) p.columns = width;
    placed.push(...group);
    group = [];
    columnEnds.length = 0;
  };

  for (const item of sorted) {
    const top = Math.max(0, (item.start.getTime() - midnight) / MINUTE);
    const bottom = Math.min(1440, (item.end.getTime() - midnight) / MINUTE);
    if (top >= groupEnd && group.length) close();
    let column = columnEnds.findIndex((end) => end <= top);
    if (column === -1) column = columnEnds.length;
    columnEnds[column] = Math.max(bottom, top + 15);
    group.push({ item, top, height: Math.max(15, bottom - top), column, columns: 1 });
    groupEnd = Math.max(groupEnd === -Infinity ? 0 : groupEnd, top + Math.max(15, bottom - top));
  }
  if (group.length) close();
  return placed;
}

/** Where on the calendar somebody clicked to book: a day, and on the hour grids a time too. */
export interface PickedTime {
  at: Date;
  withTime: boolean;
}
